import React, { useState } from 'react';
import type { FieldActivity, PeriodStats, ReportDetailLabels } from '../../../shared/types/reports';
import { getMoodBadgeStyle } from '../../../shared/utils';

export interface ActivityComparisonProps {
  currentPeriodStats: PeriodStats;
  previousPeriodStats: PeriodStats | null;
  periodType: 'week' | 'month';
  labels: ReportDetailLabels;
}

const INITIAL_DISPLAY_COUNT = 10;

export function ActivityComparison({
  currentPeriodStats,
  previousPeriodStats,
  periodType,
  labels,
}: ActivityComparisonProps) {
  const [showAll, setShowAll] = useState(false);

  // Sort activities by count (descending)
  const currentActivities = [...currentPeriodStats.fieldActivities].sort(
    (a, b) => b.count - a.count
  );

  const previousActivitiesMap = new Map(
    (previousPeriodStats?.fieldActivities || []).map(a => [a.fieldId, a])
  );

  const displayActivities = showAll
    ? currentActivities
    : currentActivities.slice(0, INITIAL_DISPLAY_COUNT);

  if (currentActivities.length === 0) {
    return (
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white border-0">
          <h5 className="mb-0">
            <i className="bi bi-list-check me-2"></i>
            {labels.topActivities}
          </h5>
        </div>
        <div className="card-body">
          <p className="text-muted mb-0">No activity data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header bg-white border-0 px-3 px-sm-4">
        <h6 className="mb-0">
          <i className="bi bi-list-check me-2"></i>
          {labels.topActivities}
        </h6>
      </div>
      <div className="card-body p-0">
        {/* Mobile list view */}
        <div className="d-md-none">
          {displayActivities.map((activity, index) => {
            const previousActivity = previousActivitiesMap.get(activity.fieldId);
            const countChange = previousActivity
              ? activity.count - previousActivity.count
              : null;
            const moodChange = previousActivity?.averageMood !== null &&
              previousActivity?.averageMood !== undefined &&
              activity.averageMood !== null
                ? activity.averageMood - previousActivity.averageMood
                : null;
            const isNew = previousActivity === undefined;

            return (
              <div
                key={activity.fieldId}
                className={`px-3 py-2 ${index !== displayActivities.length - 1 ? 'border-bottom' : ''}`}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1 me-2" style={{ minWidth: 0 }}>
                    <div className="fw-semibold text-truncate">{activity.fieldName}</div>
                    {activity.categoryName && (
                      <small className="text-muted">{activity.categoryName}</small>
                    )}
                  </div>
                  <div className="text-end" style={{ flexShrink: 0 }}>
                    <div className="d-flex align-items-center gap-2 justify-content-end">
                      <span className="text-muted small">
                        {activity.count}x
                        {isNew ? (
                          <span className="badge bg-info ms-1" style={{ fontSize: '0.65rem' }}>New</span>
                        ) : countChange !== null && countChange !== 0 ? (
                          <span className={`ms-1 ${countChange > 0 ? 'text-success' : 'text-danger'}`}>
                            ({countChange > 0 ? '+' : ''}{countChange})
                          </span>
                        ) : null}
                      </span>
                      {activity.averageMood !== null && (
                        <span
                          className="badge"
                          style={{ ...getMoodBadgeStyle(activity.averageMood), fontSize: '0.7rem' }}
                        >
                          {activity.averageMood.toFixed(0)}%
                          {moodChange !== null && Math.abs(moodChange) >= 0.5 && (
                            <span className="ms-1" style={{ opacity: 0.8 }}>
                              ({moodChange > 0 ? '+' : ''}{moodChange.toFixed(0)})
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop table view */}
        <div className="d-none d-md-block">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th className="ps-4">Activity</th>
                <th className="text-center">Count</th>
                <th className="text-center pe-4">{labels.averageMood}</th>
              </tr>
            </thead>
            <tbody>
              {displayActivities.map((activity) => {
                const previousActivity = previousActivitiesMap.get(activity.fieldId);
                const countChange = previousActivity
                  ? activity.count - previousActivity.count
                  : null;
                const moodChange = previousActivity?.averageMood !== null &&
                  previousActivity?.averageMood !== undefined &&
                  activity.averageMood !== null
                    ? activity.averageMood - previousActivity.averageMood
                    : null;
                const isNew = previousActivity === undefined;

                return (
                  <tr key={activity.fieldId}>
                    <td className="ps-4">
                      <div className="fw-semibold">{activity.fieldName}</div>
                      {activity.categoryName && (
                        <small className="text-muted">{activity.categoryName}</small>
                      )}
                    </td>
                    <td className="text-center">
                      <span className="fw-semibold">{activity.count}</span>
                      {isNew ? (
                        <span className="badge bg-info ms-2">New</span>
                      ) : countChange !== null && countChange !== 0 ? (
                        <small className={`ms-1 ${countChange > 0 ? 'text-success' : 'text-danger'}`}>
                          ({countChange > 0 ? '+' : ''}{countChange})
                        </small>
                      ) : null}
                    </td>
                    <td className="text-center pe-4">
                      {activity.averageMood !== null ? (
                        <span className="d-inline-flex align-items-center gap-1">
                          <span
                            className="badge"
                            style={getMoodBadgeStyle(activity.averageMood)}
                          >
                            {activity.averageMood.toFixed(0)}%
                          </span>
                          {moodChange !== null && Math.abs(moodChange) >= 0.5 && (
                            <small className={moodChange > 0 ? 'text-success' : 'text-danger'}>
                              ({moodChange > 0 ? '+' : ''}{moodChange.toFixed(0)}%)
                            </small>
                          )}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {currentActivities.length > INITIAL_DISPLAY_COUNT && (
          <div className="text-center py-3 border-top">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll
                ? 'Show less'
                : `Show all (${currentActivities.length})`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ActivityComparison;
